import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Ban,
  Bug,
  Columns3,
  Layers,
  Loader2,
  RefreshCw,
  SquareArrowOutUpRight,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { AIPerformanceInsights } from '~/components/common/ai-performance-insights';
import { AttributeComparisonList } from '~/components/common/attribute-comparison-list';
import { AttributeScoreList } from '~/components/common/attribute-score-list';
import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { EmptyStateOverlay } from '~/components/common/empty-state-overlay/empty-state-overlay';
import { QualityScoreMeter } from '~/components/common/quality-score-meter';
import { RD3C } from '~/components/common/rd3c';
import { RefreshIconButton } from '~/components/common/refresh-icon-button';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { LilyLogoMark } from '~/components/logos/LilyLogoMark';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Separator } from '~/components/ui/separator';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { TEMPORAL_NAMESPACE, temporalWorkflowUrl } from '~/constants/config/env';
import {
  BatchDebugPanel,
  type DebugIdEntry,
} from '~/features/catalog-operations/components/batch-detail/batch-debug-panel';
import { BatchDetailHeader } from '~/features/catalog-operations/components/batch-detail/batch-detail-header';
import { BatchNeedsAttention } from '~/features/catalog-operations/components/batch-detail/batch-needs-attention';
import { BatchPipelineStages } from '~/features/catalog-operations/components/batch-detail/batch-pipeline-stages';
import { BatchProcessingDetails } from '~/features/catalog-operations/components/batch-detail/batch-processing-details';
import { BatchRetryBanner } from '~/features/catalog-operations/components/batch-detail/batch-retry-panel';
import { BatchTopMetrics } from '~/features/catalog-operations/components/batch-detail/batch-top-metrics';
import { DeliveryApprovalPanel } from '~/features/catalog-operations/components/batch-detail/delivery-approval-panel';
import { AttributeCoverageList } from '~/features/catalog-operations/components/batch-detail/enrichment-coverage/attribute-coverage-list';
import { CategoryCoverageList } from '~/features/catalog-operations/components/batch-detail/enrichment-coverage/category-coverage-list';
import { CoverageAttributeSignalList } from '~/features/catalog-operations/components/batch-detail/enrichment-coverage/coverage-attribute-signal-list';
import { CoverageCategorySignalList } from '~/features/catalog-operations/components/batch-detail/enrichment-coverage/coverage-category-signal-list';
import { CoverageStatsRow } from '~/features/catalog-operations/components/batch-detail/enrichment-coverage/coverage-stats-row';
import { ProductDetailChangesCard } from '~/features/catalog-operations/components/batch-detail/enrichment-coverage/product-detail-changes-card';
import {
  InsightMembershipDrawer,
  type MembershipHeaderStats,
} from '~/features/catalog-operations/components/batch-detail/insight-membership-drawer';
import {
  PerformanceSignalsStatsRow,
  type PerformanceSignalsStatCard,
} from '~/features/catalog-operations/components/batch-detail/performance-signals-stats-row';
import { RunValidationStrip } from '~/features/catalog-operations/components/batch-detail/run-validation-strip';
import { ScheduledPreviewFailureBanner } from '~/features/catalog-operations/components/batch-detail/scheduled-preview-failure-banner';
import { WorkflowRetryDialog } from '~/features/catalog-operations/components/batch-detail/workflow-retry-dialog';
import { FunnelIssues } from '~/features/catalog-operations/components/batch-funnel/funnel-issues';
import {
  FunnelProductsSheet,
  type FunnelDrillTarget,
} from '~/features/catalog-operations/components/batch-funnel/funnel-products-sheet';
import { FunnelRunDetails } from '~/features/catalog-operations/components/batch-funnel/funnel-run-details';
import { FunnelVerdictBanner } from '~/features/catalog-operations/components/batch-funnel/funnel-verdict-banner';
import { BatchRetryDialog } from '~/features/catalog-operations/components/batch-retry/batch-retry-dialog';
import {
  carryoverNoteShort,
  carryoverNoteText,
  runTotal,
  leftOverFilter,
  PROCESSING_FROM_EARLIER,
  PROCESSING_MOVED_TO_LATER,
  withCarryoverRows,
} from '~/features/catalog-operations/components/batch-retry/batch-retry-utils';
import { BatchRunsTab } from '~/features/catalog-operations/components/batch-retry/batch-runs-tab';
import { ExcludeRemainingDialog } from '~/features/catalog-operations/components/batch-retry/exclude-remaining-dialog';
import { PostStepRetryDialog } from '~/features/catalog-operations/components/batch-retry/post-step-retry-dialog';
import { CatalogOperationsLayout } from '~/features/catalog-operations/components/catalog-operations-layout';
import { EnrichmentJobArchiveControl } from '~/features/catalog-operations/components/enrichment-job-archive-control';
import { ReconcileJobButton } from '~/features/catalog-operations/components/reconcile-job-button';
import { BatchTranslationsDialog } from '~/features/catalog-operations/components/translations/batch-translations-dialog';
import { RD3C_DIMENSION_LABEL_KEYS } from '~/features/catalog-operations/constants/rd3c-dimension-labels';
import { useBatchCoverageWithAutoRecompute } from '~/features/catalog-operations/hooks/use-batch-coverage';
import {
  isJobRunning,
  useBatchCoverageDetail,
  useBatchHeader,
  useBatchInsightsServerStatus,
  useBatchInsightsWithAutoRecompute,
  useBatchLiveStage,
  useBatchOptimizationQuality,
} from '~/features/catalog-operations/hooks/use-batch-detail';
import {
  isFunnelLive,
  useBatchFunnel,
  useBatchStageErrors,
  useResetFunnelBatch,
} from '~/features/catalog-operations/hooks/use-batch-funnel';
import {
  isBatchLive,
  useInvalidateBatchOnRunsTransition,
  useBatchCarryover,
  useBatchNeedsAttention,
  useBatchRuns,
  useUndoExclusion,
} from '~/features/catalog-operations/hooks/use-batch-retry';
import { useCoverageRecompute } from '~/features/catalog-operations/hooks/use-coverage-recompute';
import { useInsightsRecompute } from '~/features/catalog-operations/hooks/use-insights-recompute';
import { useScheduledBatchResume } from '~/features/catalog-operations/hooks/use-scheduled-batch-resume';
import { useScheduledJobStatus } from '~/features/catalog-operations/hooks/use-scheduled-job-status';
import { useWorkflowFailurePoints } from '~/features/catalog-operations/hooks/use-workflow-retry';
import { mergeLiveStages } from '~/features/catalog-operations/mappers/catalog-operations.mapper';
import type { BatchRetryScope } from '~/features/catalog-operations/types/batch-retry.api.types';
import type { InsightMembershipCategory } from '~/features/catalog-operations/types/catalog-operations.api.types';
import type {
  BatchDetailTab,
  BatchTopMetric,
} from '~/features/catalog-operations/types/catalog-operations.types';
import type {
  PostStepName,
  PostStepScope,
} from '~/features/catalog-operations/types/post-step-retry.api.types';
import {
  isExperimentSource,
  isFeedAuditSource,
  isTrialSource,
} from '~/features/catalog-operations/utils/batch-flow-icons';
import {
  deriveBatchRetryState,
  type NeedsAttentionClass,
} from '~/features/catalog-operations/utils/batch-retry-state';
import { getDeliveryCompareEligibility } from '~/features/catalog-operations/utils/delivery-compare-eligibility';
import {
  buildOccasionHeaderStats,
  buildTermHeaderStats,
  buildTrendHeaderStats,
  findMembershipHeaderStats,
  type MembershipReturnState,
} from '~/features/catalog-operations/utils/insight-membership';
import {
  bannerStageError,
  isBatchFailed,
  splitStageErrors,
  tallyStageErrors,
} from '~/features/catalog-operations/utils/stage-errors';
import { getRetryBlocker } from '~/features/catalog-operations/utils/workflow-retry';
import { BatchInfoSheetView } from '~/features/content-enrichment/components/batch-info-sheet';
import {
  usePauseEnrichment,
  useResumeEnrichment,
} from '~/features/content-enrichment/hooks/use-enrichment-controls';
import { CostApprovalPendingBanner } from '~/features/cost-estimate/components/cost-approval-pending-banner';
import { CostEstimateCard } from '~/features/cost-estimate/components/cost-estimate-card';
import { useCostEstimate, useCostGateSync } from '~/features/cost-estimate/hooks/use-cost-estimate';
import type { CostActualPhase } from '~/features/cost-estimate/types/cost-estimate.types';
import { resolveCostWaiting } from '~/features/cost-estimate/utils/cost-waiting';
import { ExportDownloadButton } from '~/features/exports/components/export-download-button';
import { ArchiveHistoryPopover } from '~/features/list-archive/components/archive-history-popover';
import { useCanManageArchive } from '~/features/list-archive/hooks/use-can-manage-archive';
import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';
import { useWorkflowRunDetail } from '~/features/workflow-tracking/hooks/use-workflow-run-detail';
import { useCanChooseRetailerPrompt } from '~/hooks/useCanChooseRetailerPrompt';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import { formatLocale, useTranslationLocales } from '~/hooks/useTranslationLocales';
import { useAuthStore } from '~/store/auth.store';
import { formatCompactNumber } from '~/utils/formatting';
import {
  averageAttributeCompleteness,
  countTermsMatched,
  sumNewlyMatchedSearchVolume,
} from '~/utils/insights-transforms';
import { popularAttributesShown } from '~/utils/popular-attributes';
import {
  transformDimensionScoresToRD3CItems,
  transformQualityScorePanelToItems,
} from '~/utils/quality-score-transforms';

/** `/coverage` labels for the two optional steps that are hidden when the run
 *  disabled them — the API returns every attribute regardless of batch config,
 *  so a disabled step's row is all-zero noise. Lower-cased for label matching. */
const SEARCH_DESCRIPTORS_COVERAGE_LABEL = 'search descriptors';
const PHRASE_DESCRIPTIONS_COVERAGE_LABEL = 'phrase descriptions';

/** Field labels never surfaced in the "Field Coverage — Before vs After" card,
 *  regardless of what `/coverage` returns. Matched case-insensitively against
 *  the row label; add a label here to hide that field from the section. */
const FIELD_COVERAGE_DO_NOT_DISPLAY: readonly string[] = ['Optimized Images'];

/** Field labels additionally hidden from "Field Coverage — Before vs After" on
 *  `META_ADS` runs only — Meta doesn't optimize these Google-feed fields, so
 *  their coverage rows are noise on a Meta batch. Matched case-insensitively. */
const META_FIELD_COVERAGE_IGNORE: readonly string[] = [
  'Link',
  'Additional Image Link',
  'Availability',
  'Price',
  'Price Currency',
  'Sale Price',
];

/** Loading placeholder for the Coverage Signals section — a 2×2 card grid
 *  matching the four cards below, so content doesn't jump when the async
 *  coverage rows land. */
function CoverageSignalsSkeleton() {
  return (
    <div
      data-testid="batch-detail-coverage-signals-loading"
      className="grid grid-cols-1 gap-4 lg:grid-cols-2"
    >
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="bg-background space-y-4 rounded-xl px-5 py-6">
          <Skeleton className="h-5 w-52" />
          <Skeleton className="h-4 w-72" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}

/** Progress state shown while the empty-coverage auto-recompute runs. A single
 *  focused card with a spinner and an indeterminate bar — deliberately not the
 *  four-box shimmer, which reads as "loading rows" rather than "regenerating". */
function CoverageSignalsRecomputing({ t }: { t: (key: string) => string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="batch-detail-coverage-signals-recomputing"
      className="bg-background flex flex-col items-center justify-center gap-4 rounded-xl px-6 py-16 text-center"
    >
      <span className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
        <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin" />
      </span>
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">
          {t('coverageSignals.recomputing.title')}
        </p>
        <p className="text-muted-foreground text-sm">{t('coverageSignals.recomputing.subtitle')}</p>
      </div>
      {/* Indeterminate bar — the status endpoint reports no percentage, so this
          signals ongoing activity rather than a real completion fraction. */}
      <div className="bg-muted h-1.5 w-full max-w-xs overflow-hidden rounded-full">
        <div className="bg-primary/70 h-full w-1/3 animate-pulse rounded-full" />
      </div>
    </div>
  );
}

/** Loading placeholder for the Performance Signals tab, shown while
 *  `useBatchInsights` fetches — mirrors the section header, the 4-card stats
 *  row, and the 3-section layout (popular terms table, popular attributes
 *  list, coverage & trends) so content doesn't jump around once the data
 *  resolves. */
function PerformanceSignalsSkeleton() {
  return (
    <>
      <div
        data-testid="batch-detail-performance-signals-loading"
        className="flex items-end justify-between gap-4"
      >
        <div className="space-y-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-9 w-28 shrink-0 rounded-md" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <div className="bg-background space-y-4 rounded-xl px-5 py-6">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
      <div className="bg-background space-y-4 rounded-xl px-5 py-6">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-4 w-72" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
      <div className="bg-background space-y-4 rounded-xl px-5 py-6">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-72" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    </>
  );
}

/** Progress state shown while a Performance Signals recompute runs (POST + poll).
 *  A single focused card with a spinner and an indeterminate bar — the same
 *  "regenerating" treatment as the coverage tab, deliberately not the row
 *  shimmer, which would read as a first load rather than a rebuild. */
function PerformanceSignalsRecomputing({ t }: { t: (key: string) => string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="batch-detail-performance-signals-recomputing"
      className="bg-background flex flex-col items-center justify-center gap-4 rounded-xl px-6 py-16 text-center"
    >
      <span className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
        <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin" />
      </span>
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">
          {t('performanceSignals.recomputing.title')}
        </p>
        <p className="text-muted-foreground text-sm">
          {t('performanceSignals.recomputing.subtitle')}
        </p>
      </div>
      {/* Indeterminate bar — the status endpoint reports no percentage, so this
          signals ongoing activity rather than a real completion fraction. */}
      <div className="bg-muted h-1.5 w-full max-w-xs overflow-hidden rounded-full">
        <div className="bg-primary/70 h-full w-1/3 animate-pulse rounded-full" />
      </div>
    </div>
  );
}

/** Loading placeholder for the Delivery Health tab while the phase-3 coverage
 *  read resolves — a processing-details block plus a needs-attention block, so
 *  the layout doesn't jump when the data lands. `testId` names the tab it stands in for. */
function DeliveryHealthSkeleton({ testId }: { testId: string }) {
  return (
    <>
      <Skeleton data-testid={testId} className="h-40 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </>
  );
}

/** Loading placeholder for the Optimization Quality tab while its lazy phase-4
 *  read resolves — a single tall card matching the meter + score lists. */
function OptimizationQualitySkeleton() {
  return (
    <Skeleton
      data-testid="batch-detail-optimization-quality-loading"
      className="h-[36rem] w-full rounded-xl"
    />
  );
}

/** Inline "couldn't load this section" state for a tab whose phase read failed,
 *  shown in place of that tab's body without blanking the already-painted shell. */
function TabLoadError({ t }: { t: (key: string) => string }) {
  return (
    <div
      data-testid="batch-detail-tab-error"
      className="bg-background flex items-center justify-center rounded-xl px-6 py-16 text-center"
    >
      <p className="text-muted-foreground text-sm">{t('errors.sectionLoadFailed')}</p>
    </div>
  );
}

interface Props {
  batchId: string;
  /** Tab read from the `?tab=` URL search param — keeps the active tab across
   *  reloads/deep-links. Defaults to `'delivery-health'` at the route level. */
  initialTab: BatchDetailTab;
  /** Set when this job was opened from an experiment's "View Batch Details"
   *  CTA — `batchId` is then the experiment id. Scopes the not-found →
   *  toast + redirect behavior below to that flow only. */
  source?: 'experiment';
  /** Insight drawer to re-open on arrival — set when returning from a product
   *  opened from that drawer. Read once, into initial state. */
  restoreMembership?: MembershipReturnState;
}

export function CatalogBatchDetailPage({ batchId, initialTab, source, restoreMembership }: Props) {
  const { t } = useTranslation('catalog-operations');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setActiveTab] = useState<BatchDetailTab>(initialTab);
  const [isDeliveryApprovalOpen, setIsDeliveryApprovalOpen] = useState(false);
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false);
  const [isRecomputeDialogOpen, setIsRecomputeDialogOpen] = useState(false);
  const [isCoverageRecomputeDialogOpen, setIsCoverageRecomputeDialogOpen] = useState(false);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  // Product Term / Occasion / Trend row → insight-membership drawer.
  // `selectedMembership` stays set across a close so the closing animation
  // doesn't show a blank drawer.
  // A restored drawer starts open with no header stats — they're rebuilt from
  // the insights by label below once those load (see `membershipHeaderStats`).
  const [isMembershipDrawerOpen, setIsMembershipDrawerOpen] = useState(!!restoreMembership);
  const [selectedMembership, setSelectedMembership] = useState<{
    category: InsightMembershipCategory;
    label: string;
    headerStats: MembershipHeaderStats | null;
    initialGroup?: MembershipReturnState['group'];
  } | null>(
    restoreMembership
      ? {
          category: restoreMembership.category,
          label: restoreMembership.label,
          headerStats: null,
          initialGroup: restoreMembership.group,
        }
      : null,
  );
  // Coverage & Trends legend filter: null shows both series (default); soloing
  // one shows only that bar across both the Occasions and Trends lists.
  const [insightsSolo, setInsightsSolo] = useState<'before' | 'after' | null>(null);
  const insightsBeforeVisible = insightsSolo !== 'after';
  const insightsAfterVisible = insightsSolo !== 'before';

  // Debug panel — a super-admin-only troubleshooting aid surfacing the raw
  // response IDs. Gated by the `debugger-int` flag AND super-admin so it never
  // reaches customers; both must be true for the header affordance to render.
  const { isAllowed: isDebuggerAllowed } = useFeatureGate('debugger-int');
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const showDebug = isDebuggerAllowed && isSuperAdmin;
  const canSeeRetailerPrompt = useCanChooseRetailerPrompt();
  // Compare Delivery entry — the gate check alone (cheap, no header dependency);
  // eligibility (needs the resolved header) is derived further below, once
  // `header` is narrowed past the loading/error guards.
  const { isAllowed: isCompareDeliveryAllowed } = useFeatureGate('compare-delivery');
  // Pre-Delivery Wizard — when on, "Review" navigates to the routed wizard
  // instead of opening the side panel below. Off = today's panel, unchanged.
  const { isAllowed: isDeliveryWizardAllowed } = useFeatureGate('delivery-wizard');
  // List Archive: super / org admins can archive a finished batch from here, or
  // unarchive one they deep-linked into. The control patches the cached header.
  const canManageArchive = useCanManageArchive();
  // Batch Details Funnel — verdict, funnel, outcomes and issues from one read,
  // shown alongside the existing pipeline cards. Off = page unchanged.
  const { isAllowed: isFunnelAllowed } = useFeatureGate('batch_funnel');
  const { data: funnel, isError: isFunnelError } = useBatchFunnel(batchId, isFunnelAllowed);
  // Stage errors (BE PR #990) on the funnel's cadence. A failed batch shows every
  // entry as current; otherwise ERRORs belong to earlier attempts (§5.5).
  const { data: stageErrors } = useBatchStageErrors(batchId, isFunnelLive(funnel), isFunnelAllowed);
  const batchFailed = isBatchFailed(funnel);
  const { current: currentStageErrors, earlier: earlierStageErrors } = splitStageErrors(
    stageErrors?.errors ?? [],
    batchFailed,
  );
  const bannerError = bannerStageError(currentStageErrors, batchFailed);
  // Run Details holds the batch's runs (root + retries, not for experiment-hosted batches) and,
  // behind the funnel gate, the product funnel; an old `?tab=runs` link lands there too. With
  // neither to show, a `?tab=run-details` deep link falls back to the default tab.
  // Batch pause / retry: root run + retry children. Hidden for experiment-hosted batches and
  // for backends without the runs API (404 → the legacy page).
  const { data: batchRuns, isError: batchRunsError } = useBatchRuns(batchId, !source);
  useInvalidateBatchOnRunsTransition(batchRuns);
  // Carry-over between batches; null on older backends, in which case nothing new renders.
  const { data: carryover } = useBatchCarryover(batchId, !source);
  const showRunsSection = !source && !batchRunsError;
  const showRunDetails = isFunnelAllowed || showRunsSection;
  const wantedTab: BatchDetailTab = selectedTab === 'runs' ? 'run-details' : selectedTab;
  const activeTab: BatchDetailTab =
    wantedTab === 'run-details' && !showRunDetails ? 'delivery-health' : wantedTab;
  // "Is it stuck?" — run health from `links.health`, read only while the batch moves.
  const { data: funnelRunDetail } = useWorkflowRunDetail(
    funnel?.workflowId ?? '',
    isFunnelLive(funnel),
  );
  // Funnel drill-in (chip / outcome / issue → products sheet) and the Reset confirm.
  const [funnelDrill, setFunnelDrill] = useState<FunnelDrillTarget | null>(null);
  const [isFunnelResetOpen, setIsFunnelResetOpen] = useState(false);
  const resetFunnel = useResetFunnelBatch(batchId, {
    success: t('batchFunnel.reset.requested'),
    error: t('batchFunnel.reset.error'),
  });

  /** Opens delivery review — the routed wizard when gated on, else the side panel. */
  function handleReviewDelivery() {
    if (isDeliveryWizardAllowed) {
      void navigate({ to: '/catalog-operations/$batchId/deliver', params: { batchId } });
      return;
    }
    setIsDeliveryApprovalOpen(true);
  }

  /** Updates local tab state and mirrors it into the URL's `tab` search param
   *  so a reload or shared link lands back on the same tab. */
  /** Closing the drawer also drops a restored drawer's URL params, so a reload
   *  afterwards doesn't pop it back open. */
  function handleMembershipDrawerOpenChange(open: boolean) {
    setIsMembershipDrawerOpen(open);
    if (open || !restoreMembership) return;
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId },
      search: (prev) => ({
        ...prev,
        membership: undefined,
        membershipLabel: undefined,
        membershipGroup: undefined,
      }),
      replace: true,
    });
  }

  function handleTabChange(tab: BatchDetailTab) {
    setActiveTab(tab);
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId },
      // Run/landing params belong to Run Details; never carry them to another tab.
      search: (prev) => ({ ...prev, tab, run: undefined, cls: undefined, na: undefined }),
    });
  }

  // Batch pause / retry: root run + retry children, classified Needs Attention. The server
  // decides which actions exist (`availableActions`); older backends 404 → legacy page.
  const { data: batchNeedsAttention } = useBatchNeedsAttention(
    batchId,
    !source && !!batchRuns,
    isBatchLive(batchRuns),
  );
  const retryState = batchRuns ? deriveBatchRetryState(batchRuns) : null;
  const carryoverNote = carryoverNoteShort(carryover);
  const carryoverTitle = carryoverNoteText(carryover) ?? undefined;
  // A retry asks for cost approval under its own workflow id; the batch's own cost banner
  // watches the original run, so read the running retry's estimate separately.
  const activeRetryRunId = batchRuns?.activeRetryRunId ?? null;
  const { data: retryCostEstimate } = useCostEstimate(activeRetryRunId, {
    runActive: activeRetryRunId !== null,
  });
  const retryAwaitingCostApproval = retryCostEstimate?.status === 'PENDING_APPROVAL';
  // Pause / resume address the root run; the server also pauses a running retry from it.
  const rootRunId = batchRuns?.runs.find((r) => r.kind === 'ROOT')?.runId ?? '';
  const refreshBatchRuns = () => {
    void queryClient.invalidateQueries({
      queryKey: [...queryKeys.catalogOperations.all, 'batches', batchId],
    });
  };
  const pauseBatch = usePauseEnrichment(rootRunId, refreshBatchRuns);
  const resumeBatch = useResumeEnrichment(rootRunId, refreshBatchRuns);
  const undoExclusion = useUndoExclusion(batchId);
  const [batchRetryDialog, setBatchRetryDialog] = useState<{
    open: boolean;
    scope?: BatchRetryScope;
    fromRunId?: string;
  }>({ open: false });
  const [postStepRetry, setPostStepRetry] = useState<{
    open: boolean;
    step: PostStepName;
    scope?: PostStepScope;
  }>({ open: false, step: 'SEARCH_DESCRIPTORS' });
  const [isExcludeOpen, setIsExcludeOpen] = useState(false);
  // A "not enriched yet" link → scroll the Runs panel's product grid into view once it renders.
  const [focusRunProducts, setFocusRunProducts] = useState(false);
  const clearFocusRunProducts = useCallback(() => {
    setFocusRunProducts(false);
  }, []);

  /**
   * "N not enriched yet" anywhere on the page → the Runs panel, latest run, on what it left. The
   * latest run's grid already lists every product still not enriched (a retry picks up all the
   * previous run left), so there is no separate drawer. Not the products page's Needs Attention
   * tab: that lists curation's review flag, a different set from these counts.
   */
  function showNeedsAttentionProducts(cls: NeedsAttentionClass = 'ALL') {
    const latest = batchRuns?.runs[batchRuns.runs.length - 1];
    setActiveTab('run-details');
    setFocusRunProducts(true);
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId },
      search: (prev) => ({
        ...prev,
        tab: 'run-details',
        run: latest?.runId,
        cls: cls !== 'ALL' ? cls : latest ? leftOverFilter(latest) : undefined,
        na: undefined,
      }),
    });
  }

  // The Enriched card's "from / moved to … batches" link → Runs, carry-over section in view.
  const [focusCarryover, setFocusCarryover] = useState(false);
  const clearFocusCarryover = useCallback(() => {
    setFocusCarryover(false);
  }, []);

  function showCarryover() {
    setActiveTab('run-details');
    setFocusCarryover(true);
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId },
      search: (prev) => ({
        ...prev,
        tab: 'run-details',
        run: batchRuns?.runs.find((r) => r.kind === 'ROOT')?.runId,
        cls: undefined,
        na: undefined,
      }),
    });
  }

  function openBatchRetry(options: { scope?: BatchRetryScope; fromRunId?: string } = {}) {
    setBatchRetryDialog({ open: true, ...options });
  }

  /** Run chip → Runs tab; a retry chip also opens that run's drawer (`run=<runId>`). */
  function handleSelectRun(runId: string | null) {
    setActiveTab('run-details');
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId },
      search: (prev) => ({
        ...prev,
        tab: 'run-details',
        run: runId ?? undefined,
        cls: undefined,
        na: undefined,
      }),
    });
  }

  const detailTabs = [
    { value: 'delivery-health' as const, label: t('tabs.deliveryHealth') },
    { value: 'performance-signals' as const, label: t('tabs.performanceSignals') },
    { value: 'enrichment-coverage' as const, label: t('tabs.enrichmentCoverage') },
    { value: 'optimization-quality' as const, label: t('tabs.optimizationQuality') },
    ...(showRunDetails ? [{ value: 'run-details' as const, label: t('tabs.runDetails') }] : []),
  ];
  // Phase 1 (shell) — the fast, DB-light header the page shell paints from.
  const { data: header, isLoading, isError, error } = useBatchHeader(batchId);
  // Trial batches render the legacy Coverage by Category / Product Attribute
  // cards instead of the batch-scoped Coverage Signals section, so the coverage
  // fetch below is skipped for them.
  const isTrialBatch = header ? isTrialSource(header.triggerSource) : false;
  // Phase 2 (live progress) — refines the pipeline stages while the job runs;
  // `204` (→ null) for terminal jobs falls back to the header timeline.
  const { data: liveStage } = useBatchLiveStage(batchId, isJobRunning(header?.status));
  // Cost gate: the run is live while the header says running and live-stage has not answered
  // 204 (workflow gone). A run parked at its gate answers 200 with `costGate` even when it has
  // no live stage, so it stays active and live-stage keeps polling. Shares the card's query, so
  // this adds no request.
  const costRunActive = isJobRunning(header?.status) && liveStage !== null;
  const costHostId = header ? (header.workflowId ?? header.batchId) : null;
  const { data: costEstimate } = useCostEstimate(costHostId, { runActive: costRunActive });
  // `costGate` changes (appear / clear) refresh the estimate straight away. Live-stage polls
  // every few seconds while the run is live, so it sees the gate first; the header covers a
  // backend that does not send it on live-stage (always null there) and the at-rest page.
  useCostGateSync(costHostId, header?.costGate);
  useCostGateSync(costHostId, liveStage?.costGate);
  // The reverse: the header polls only while it already carries a `costGate`, so a gate that
  // opens (or closes) after load is first seen by the estimate poll — refetch the header then.
  const costPending =
    costEstimate === undefined ? undefined : costEstimate?.status === 'PENDING_APPROVAL';
  const prevCostPending = useRef(costPending);
  useEffect(() => {
    const prev = prevCostPending.current;
    prevCostPending.current = costPending;
    if (costPending === undefined || prev === undefined || prev === costPending) return;
    void queryClient.invalidateQueries({
      queryKey: queryKeys.catalogOperations.batchHeader(batchId),
    });
  }, [costPending, batchId, queryClient]);
  // Header chip / note: the fetched estimate wins; live-stage's costGate (freshest), then the
  // header's, cover the rest.
  const costWaiting = resolveCostWaiting(liveStage?.costGate ?? header?.costGate, costEstimate);
  // Phase 3 (coverage tabs) — Delivery Health + Enrichment Coverage. Depends on
  // the header, so it fires the moment the fast shell call resolves; its own tab
  // bodies skeleton until it lands.
  const {
    data: coverageDetail,
    isLoading: isCoverageDetailLoading,
    isError: isCoverageDetailError,
  } = useBatchCoverageDetail(batchId, header);
  // Phase 4 (heaviest, lazy) — only fetched once the operator opens its tab.
  const { data: optimization, isLoading: isOptimizationLoading } = useBatchOptimizationQuality(
    batchId,
    activeTab === 'optimization-quality',
  );

  // When opened from an experiment, a not-found job means the enrichment
  // pipeline hasn't produced one yet — bounce back to the catalog-operations
  // list with a toast instead of stranding the user on an error page for a
  // job that may never exist under that id. Direct catalog-operations links
  // keep the inline "Failed to load" state below unchanged.
  const notFoundHandledRef = useRef(false);
  useEffect(() => {
    if (source !== 'experiment') return;
    if (notFoundHandledRef.current) return;
    const isNotFound =
      error instanceof ApiError && (error.code === 'NOT_FOUND' || error.statusCode === 404);
    if (!isNotFound) return;
    notFoundHandledRef.current = true;
    toast.error(t('errors.jobNotFound'));
    void navigate({ to: '/catalog-operations', replace: true });
  }, [source, error, navigate, t]);
  // Insights endpoint keys on the workflow id (`detail.batchId`), not the job
  // uuid route param — stays disabled until batch detail resolves it. Fetched
  // unconditionally (not gated on the tab being active) because the "Trends
  // Applied" top metric card also reads from it and renders on page load. On a
  // fresh batch `/insights` resolves all-empty (the async callback hasn't filled
  // it yet), so this self-heals with a single auto-recompute — see the hook.
  // Only self-heal empty insights once enrichment has actually produced output:
  // while a run is still moving (or failed before enrichment) an empty payload is
  // expected and there's nothing to recompute from. Insights are still fetched for
  // the top metric cards; only the auto-recompute waits for this.
  const enrichmentComplete =
    header?.stages.some((s) => s.key === 'enriched' && s.state === 'complete') ?? false;
  const {
    insights,
    isLoading: isInsightsLoading,
    isRecomputing: isInsightsAutoRecomputing,
  } = useBatchInsightsWithAutoRecompute(
    header?.batchId ?? '',
    header?.connectorCode ?? '',
    !!header?.batchId,
    enrichmentComplete,
  );
  // Coverage signal cards, keyed on the same workflow id. Gated on the tab
  // being active — unlike insights nothing outside that tab reads them, so
  // there's no reason to spend the request on page load. Written by an async
  // callback just after the run completes, hence the bounded poll.
  const {
    coverage,
    isLoading: isCoverageLoading,
    isAwaitingRows: isAwaitingCoverage,
    isRecomputing: isCoverageAutoRecomputing,
  } = useBatchCoverageWithAutoRecompute(
    header?.batchId ?? '',
    header?.connectorCode ?? '',
    !!header?.batchId && activeTab === 'enrichment-coverage' && !isTrialBatch,
  );
  // Discovers the failure point to retry from as soon as a failed batch loads
  // — keeps the Retry button immediately actionable instead of fetching on click.
  const {
    data: failurePoints,
    isLoading: isFailurePointsLoading,
    isError: isFailurePointsError,
  } = useWorkflowFailurePoints(
    header?.batchId ?? '',
    // The funnel may allow a retry on a batch the header doesn't mark failed.
    header?.status === 'failed' || funnel?.actions?.retryAllowed === true,
  );
  // Drives the Popular Product Terms empty state: a connected-but-empty batch
  // shows the generic "no data yet" message, while a never-connected tenant
  // is pointed at the Google Ads onboarding flow instead. Defaults to `true`
  // (connected) while the connectors list is loading/unresolved so a slow
  // query never flashes the "connect" prompt for an already-connected tenant.
  // Re-runs the insights workflow for this batch and refreshes the tab once it
  // settles. Keyed on the workflow id, same as the insights read it refreshes.
  const { isRecomputing, start: startRecompute } = useInsightsRecompute(
    header?.batchId ?? '',
    header?.connectorCode ?? '',
  );
  // Server-authoritative recompute status (contract §5.5). Complements the
  // client-side recompute above: on a mid-recompute page refresh it re-reads
  // RECOMPUTING from the server and restores the spinner, then refetches the
  // insights once it settles. ORed into the tab's recomputing state below.
  const { isServerRecomputing } = useBatchInsightsServerStatus(
    header?.batchId ?? '',
    !!header?.batchId,
  );
  // Coverage's own recompute. Separate hook and separate pending flag from the
  // insights one above: they hit different integration-service resources and
  // invalidate different queries, so one shared spinner would lie about which
  // tab's data is being rewritten.
  const { isRecomputing: isCoverageRecomputing, start: startCoverageRecompute } =
    useCoverageRecompute(header?.batchId ?? '', header?.connectorCode ?? '');
  const { data: connectedConnectors } = useConnectedConnectors();
  const isGoogleAdsConnected = connectedConnectors
    ? connectedConnectors.some((c) => c.code === 'GOOGLE_ADS')
    : true;
  // Resumes this batch into the staged content-enrichment funnel: seeds the
  // job-status cache and resolves the deepest active step. Keyed on the workflow
  // id (`detail.batchId`, the `scheduled-job-…` slug) — the status endpoint 404s
  // on the job uuid. `sub` is auto-retained by the root router.
  const { resume: resumeEnrichmentWorkflow, resumingId } = useScheduledBatchResume();

  // A scheduled run can report a per-kind preview FAILURE (empty/stale source)
  // while the parent status still rolls up to COMPLETED because the pipeline
  // delivered — the parent DTO never carries the per-kind failureReason. Fetch the
  // aggregate status for funnel batches (scheduled/manual; trial/feed-audit/
  // experiment run their own wizards and 404 here) so the banner can surface it.
  // Keyed on the workflow slug (`header.batchId`); the job-uuid route param 404s.
  const isEnrichmentFunnelBatch =
    !!header &&
    !isExperimentSource(header.triggerSource) &&
    !isFeedAuditSource(header.triggerSource) &&
    !isTrialSource(header.triggerSource);
  const { data: scheduledJobStatus } = useScheduledJobStatus(
    header?.batchId ?? '',
    isEnrichmentFunnelBatch,
  );

  // Validation strip + drawer (shared with the batch export page and the delivery wizard).

  /** Why the funnel's Retry can't act even when the server allows it: discovery
   *  failed, history is past retention, or there's no point to reset to. */
  const retryBlocker = getRetryBlocker(failurePoints);
  const funnelRetryUnavailableReason = isFailurePointsError
    ? t('retry.pointsError')
    : retryBlocker?.kind === 'history-unavailable'
      ? (retryBlocker.reason ?? t('retry.historyUnavailable'))
      : retryBlocker?.kind === 'no-failure-point'
        ? t('retry.noFailurePoint')
        : null;

  /** Opens the funnel products sheet for a stage reason, outcome row or issue. */
  function openFunnelDrill(title: string, query: FunnelDrillTarget['query']) {
    setFunnelDrill({ title, query });
  }

  /** Routes each drill-in row label to the products page with the correct tab.
   *  `curationRefId` is no longer threaded through the URL — the products
   *  page reads it from its own `useBatchHeader(batchId)` query. */
  function handleDrillIn(label: string) {
    const TAB_MAP: Record<string, string> = {
      'Newly Enriched Products': 'newly-enriched',
      'Needs Attention': 'needs-attention',
    };
    if (label === PROCESSING_FROM_EARLIER || label === PROCESSING_MOVED_TO_LATER) {
      showCarryover();
      return;
    }
    const tab = TAB_MAP[label];
    if (tab) {
      void navigate({
        to: '/catalog-operations/$batchId/products',
        params: { batchId },
        search: { tab: tab as 'newly-enriched' | 'needs-attention' },
      });
    }
  }

  // Names for the Languages chip ("UK English (en-GB)"); fetched only once a run has a
  // translated locale, since an English-only run's chip is just "English (en-US)".
  const localeNames = useTranslationLocales((header?.locales.length ?? 0) > 1);

  if (isLoading) {
    return (
      <CatalogOperationsLayout testId="batch-detail-layout">
        <div data-testid="batch-detail-loading" className="mx-auto max-w-7xl space-y-6">
          <Skeleton className="h-12 w-full rounded-xl" />
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </CatalogOperationsLayout>
    );
  }

  if (isError || header === undefined) {
    return (
      <CatalogOperationsLayout testId="batch-detail-layout">
        <div
          data-testid="batch-detail-error"
          className="flex min-h-full items-center justify-center"
        >
          <p className="text-muted-foreground text-sm">
            Failed to load batch details. Please try again.
          </p>
        </div>
      </CatalogOperationsLayout>
    );
  }

  // Live-refined pipeline timeline: live-stage counters overlaid on the header's
  // retrospective stages while the job runs, or the header stages as-is once it
  // settles (live-stage 204s → null).
  const stages = mergeLiveStages(header.stages, liveStage);
  // Cost gate — keyed on the run's workflow id. The estimate polls while the run is live
  // and the gate undecided (`costRunActive`), independent of the stage timeline: the header is
  // not polled mid-run, so its stages are too stale to say when the gate fires.
  const costWorkflowId = header.workflowId ?? header.batchId;
  // Actual LLM spend exists once the Enriched stage has started: poll while it is active,
  // fetch once more after it settles (complete / failed, or the job is no longer running).
  const enrichedState = stages.find((s) => s.key === 'enriched')?.state;
  const costEnrichmentPhase: CostActualPhase | null =
    enrichedState === 'active' || enrichedState === 'complete' || enrichedState === 'failed'
      ? enrichedState === 'active' && isJobRunning(header.status)
        ? 'running'
        : 'done'
      : null;

  // Compare Delivery — shown once the Delivered stage is actually complete
  // (not merely `status === 'delivered'`, which per
  // docs/pipeline-stage-terminal-states.md can mean "finished without ever
  // pushing a feed") and a delivery job id is known. See
  // `getDeliveryCompareEligibility` for the full rule.
  const canCompareDelivery =
    isCompareDeliveryAllowed && getDeliveryCompareEligibility(header).eligible;
  function handleOpenCompare() {
    void navigate({
      to: '/catalog-operations/$batchId/compare-delivery',
      params: { batchId },
    });
  }

  const notFullyProcessed = header.status === 'in-progress';
  // True when the job failed before any stage ran — every stage is still in its
  // pending/scheduled state, or was marked skipped because the failure stopped
  // the run before it could start. (`skipped` used to arrive here as `pending`;
  // it is now its own state, so both must be counted as "never ran".) In this
  // case there is no enrichment data to show, so the tabs are hidden entirely.
  const isUnprocessedFailure =
    header.status === 'failed' &&
    stages.every((s) => s.state === 'pending' || s.state === 'skipped');

  // Recompute re-derives performance/coverage signals from the batch's enrichment
  // output, so it's meaningless when the run failed at or before enrichment (the
  // enrichment stage never completed — there's nothing to recompute from). Hide the
  // Recompute button on both tabs in that case. A failure *after* enrichment
  // (e.g. delivery) keeps it, since enriched data exists.
  const failedBeforeEnrichment =
    header.status === 'failed' && stages.find((s) => s.key === 'enriched')?.state !== 'complete';

  // The Performance Signals tab shows its "recomputing" state for the manual
  // client-triggered recompute, a server-reported one (§5.5, restores the spinner
  // on a mid-recompute refresh), or the one-shot empty-insights auto-recompute.
  const isInsightsRecomputing = isRecomputing || isServerRecomputing || isInsightsAutoRecomputing;

  // All three cards are sourced from the insights endpoint's TOP_TRENDS /
  // PRODUCT_TERM / OCCASIONS_COVERAGE data — each shows '—' until insights
  // resolves, and stays '—' when its count is null (no rows: the signal was not
  // computed for this batch). A real 0 still renders as 0.
  const formatCount = (count: number | null | undefined): string =>
    count == null ? '—' : count.toLocaleString();
  const topMetrics: BatchTopMetric[] = [
    {
      label: 'Trends Applied',
      value: formatCount(insights?.trendsAppliedCount),
      tooltip:
        'Number of trend signals applied to enrich product titles, descriptions, & search terms in this batch.',
    },
    {
      label: 'Search Terms Covered',
      value: formatCount(insights?.searchTermsCoveredCount),
      tooltip:
        'Total search terms now mapped to products in this batch after enrichment. Higher coverage improves ad relevance and reach.',
    },
    {
      label: 'Occasion Tags Applied',
      value: formatCount(insights?.occasionTagsAppliedCount),
      tooltip:
        'Contextual occasion and use-case tags added to products to improve category relevance and seasonal discoverability.',
    },
  ];

  // Performance Signals tab's own 4-card summary. Distinct from `topMetrics`
  // above: these read term/attribute breadth and volume rather than raw
  // application counts, and live inside the tab instead of above it.
  // `coverageDetail` is Phase 3, fetched unconditionally on header resolve, so
  // it's already available whichever tab is active. This block only renders
  // once `isInsightsLoading` is false (see the tab body below), so `insights`
  // is settled here — `?? []` covers the residual "query resolved but empty"
  // case the same way the rest of this tab's rows already do.
  const termsMatched = countTermsMatched(insights?.popularTerms ?? []);
  const newSearchVolume = sumNewlyMatchedSearchVolume(insights?.popularTerms ?? []);
  // Measured over the attributes the card's "All" view shows — one per
  // attribute, summed across categories — not every raw row, which would
  // count each attribute once per category plus its batch-wide twin.
  const shownAttributes = popularAttributesShown(
    insights?.popularAttributes ?? [],
    insights?.attributeCategorySizes,
  );
  const attributeCompleteness = averageAttributeCompleteness(shownAttributes);
  const attributeCount = shownAttributes.length;

  const performanceCards: PerformanceSignalsStatCard[] = [
    {
      kind: 'value',
      label: t('performanceSignals.stats.productsEnriched.label'),
      tooltip: t('performanceSignals.stats.productsEnriched.tooltip'),
      description: t('performanceSignals.stats.productsEnriched.description'),
      value:
        coverageDetail?.productsEnriched !== undefined
          ? coverageDetail.productsEnriched.toLocaleString()
          : '—',
      ...(coverageDetail?.productsEnriched !== undefined &&
        header.productsCuratedTotal && {
          valueSuffix: t('performanceSignals.stats.productsEnriched.ofTotal', {
            total: header.productsCuratedTotal.toLocaleString(),
          }),
        }),
    },
    {
      kind: 'delta',
      label: t('performanceSignals.stats.termsMatched.label'),
      tooltip: t('performanceSignals.stats.termsMatched.tooltip'),
      description: t('performanceSignals.stats.termsMatched.description'),
      before: termsMatched.before.toLocaleString(),
      after: termsMatched.after.toLocaleString(),
      delta: termsMatched.after - termsMatched.before,
    },
    {
      kind: 'value',
      label: t('performanceSignals.stats.newSearchVolume.label'),
      tooltip: t('performanceSignals.stats.newSearchVolume.tooltip'),
      description:
        newSearchVolume.termCount > 0
          ? t('performanceSignals.stats.newSearchVolume.description', {
              count: newSearchVolume.termCount,
            })
          : t('performanceSignals.stats.newSearchVolume.descriptionEmpty'),
      value: formatCompactNumber(newSearchVolume.volume),
    },
    attributeCompleteness
      ? {
          kind: 'delta',
          label: t('performanceSignals.stats.attributeCompleteness.label'),
          tooltip: t('performanceSignals.stats.attributeCompleteness.tooltip'),
          description: t('performanceSignals.stats.attributeCompleteness.description', {
            count: attributeCount,
          }),
          before: `${String(attributeCompleteness.before)}%`,
          after: `${String(attributeCompleteness.after)}%`,
          delta: attributeCompleteness.after - attributeCompleteness.before,
          deltaUnit: t('performanceSignals.stats.attributeCompleteness.deltaUnit'),
        }
      : {
          kind: 'value',
          label: t('performanceSignals.stats.attributeCompleteness.label'),
          tooltip: t('performanceSignals.stats.attributeCompleteness.tooltip'),
          description: t('performanceSignals.stats.attributeCompleteness.description', {
            count: attributeCount,
          }),
          value: '—',
        },
  ];

  // Trend rows carry a `trendKind` (micro/macro) merged from the insights
  // endpoint's TOP/MICRO/MACRO groups; map it to a localized badge label the
  // comparison list renders as a thin pill (top trends stay unbadged).
  const trendItems = (insights?.trends ?? []).map((item) => {
    if (item.trendKind === 'micro') {
      return { ...item, badge: t('performanceSignals.coverageAndTrends.trends.badges.micro') };
    }
    if (item.trendKind === 'macro') {
      return { ...item, badge: t('performanceSignals.coverageAndTrends.trends.badges.macro') };
    }
    return item;
  });

  // Opens this batch's source-specific enrichment workflow. Feed Audit and Trial
  // runs have their own wizards; every other (scheduled/manual catalog) batch
  // resumes into the staged content-enrichment funnel via the workflow id
  // (`detail.batchId`) — NOT the job uuid route param, which 404s the status API.
  // Fields are read into consts here (the narrowed linear flow) so the nested
  // handler doesn't reference `detail`, whose narrowing wouldn't hold in a closure.
  const enrichmentPlatform = header.sourcePlatform.toUpperCase() === 'META' ? 'meta' : 'google';
  const enrichmentTriggerSource = header.triggerSource;
  const enrichmentWorkflowId = header.batchId;
  function handleOpenWorkflow() {
    // Experiment-sourced batches don't own a catalog-ops enrichment wizard — their
    // workflow lives on the experiment timeline. The batch is looked up by the
    // experiment id, so the route `batchId` is that id (see the page's `source`
    // note); route back to `/experiments/{expId}` with the timeline tab active.
    if (isExperimentSource(enrichmentTriggerSource)) {
      void navigate({
        to: '/experiments/$id',
        params: { id: batchId },
        search: { tab: 'timeline' },
      });
      return;
    }
    if (isFeedAuditSource(enrichmentTriggerSource)) {
      void navigate({ to: '/feed-audit/$id', params: { id: batchId } });
      return;
    }
    if (isTrialSource(enrichmentTriggerSource)) {
      void navigate({
        to: '/trial/$platform/enrichment/$id',
        params: { platform: enrichmentPlatform, id: enrichmentWorkflowId },
      });
      return;
    }
    void resumeEnrichmentWorkflow(enrichmentWorkflowId, enrichmentPlatform);
  }

  // CSV-export inputs, derived exactly as the products page does so the header
  // export triggers the same run export. `name` can be an empty string, so fall
  // back to the batch id for the label.
  const exportLabel = header.name || header.batchId;
  const exportConnector: 'META' | 'GMC' =
    header.connectorCode && /META|FACEBOOK/i.test(header.connectorCode) ? 'META' : 'GMC';

  // Raw identifiers surfaced in the super-admin debug panel. Labels use the raw
  // API field names on purpose — this is a troubleshooting view, not customer UI.
  // `id` is the route uuid the page was opened with; the rest come off the detail.
  const debugIds: DebugIdEntry[] = [
    { label: 'id', value: batchId },
    { label: 'batchId', value: header.batchId },
    // The two workflow ids carry a Temporal Web UI deep-link into the
    // `agentic-workflow` namespace (where the batch pipeline runs, not the
    // default `agentic-integrations`); the helper returns null — and the link
    // self-hides — when the id is absent.
    {
      label: 'workflowId',
      value: header.workflowId ?? null,
      href: temporalWorkflowUrl(header.workflowId, TEMPORAL_NAMESPACE.workflow),
    },
    {
      label: 'pipelineWorkflowId',
      value: header.pipelineWorkflowId ?? null,
      href: temporalWorkflowUrl(header.pipelineWorkflowId, TEMPORAL_NAMESPACE.workflow),
    },
    { label: 'importRefId', value: header.importRefId ?? null },
    { label: 'curationRefId', value: header.curationRefId ?? null },
    { label: 'deliveryJobId', value: header.deliveryJobId ?? null },
  ];

  // The /coverage API lists every attribute irrespective of what the batch
  // enabled, so hide the rows for optional steps this run explicitly disabled
  // (their coverage is all-zero noise). Only an explicit `false` hides a step;
  // `null`/absent (unknown, or a legacy batch without enrichmentConfig) leaves
  // the row visible. Applies to the Coverage-by-Attribute and Attribute-Depth
  // cards — the only ones that carry these step labels.
  const disabledStepLabels = new Set<string>();
  if (header.searchDescriptorsEnabled === false) {
    disabledStepLabels.add(SEARCH_DESCRIPTORS_COVERAGE_LABEL);
  }
  if (header.phraseDescriptionsEnabled === false) {
    disabledStepLabels.add(PHRASE_DESCRIPTIONS_COVERAGE_LABEL);
  }
  const isCoverageRowVisible = (label: string) =>
    !disabledStepLabels.has(label.trim().toLowerCase());
  // META_ADS runs reshape the Enrichment Coverage tab: they drop the Product
  // Detail Changes card, widen Category Enrichment Rate to full width, and hide
  // the Google-feed field rows Meta never optimizes (see META_FIELD_COVERAGE_IGNORE).
  const isMetaAds = header.useCase === 'META_ADS';
  const coverageAttributeRows = (coverage?.attributes ?? []).filter((row) => {
    if (!isCoverageRowVisible(row.label)) return false;
    const normalizedLabel = row.label.trim().toLowerCase();
    if (FIELD_COVERAGE_DO_NOT_DISPLAY.some((hidden) => hidden.toLowerCase() === normalizedLabel)) {
      return false;
    }
    if (
      isMetaAds &&
      META_FIELD_COVERAGE_IGNORE.some((hidden) => hidden.toLowerCase() === normalizedLabel)
    ) {
      return false;
    }
    return true;
  });

  return (
    <CatalogOperationsLayout testId="batch-detail-layout">
      <div data-testid="batch-detail-page" className="mx-auto flex h-full min-h-0 w-auto flex-col">
        {/* Pinned header — the breadcrumb + title + actions stay put while the
            metrics, pipeline, tabs and content below scroll in their own region. */}
        <div className="shrink-0 pb-6">
          <BatchDetailHeader
            detail={header}
            costWaiting={costWaiting}
            {...(batchRuns && retryState
              ? {
                  retry: {
                    runs: batchRuns,
                    state: retryState,
                    onPause: () => {
                      pauseBatch.mutate();
                    },
                    onResume: () => {
                      resumeBatch.mutate();
                    },
                    pauseResumePending: pauseBatch.isPending || resumeBatch.isPending,
                    onExclude: () => {
                      setIsExcludeOpen(true);
                    },
                    onSelectRun: handleSelectRun,
                  },
                }
              : {})}
            {...(source ? { source } : {})}
            action={
              <>
                {/* Batch Info — the same structured summary sheet as the
                  content-enrichment funnel, fed from the already-resolved header
                  so it opens without a second (aggregate) fetch. Sized to match
                  the header's icon-button cluster. Targeting = the header's stored filters. */}
                <BatchInfoSheetView
                  batchId={header.batchId}
                  data={header}
                  isLoading={false}
                  isError={false}
                  filters={header.filters ?? {}}
                  triggerVariant="icon"
                  className="h-8 w-8"
                />
                {/* Which google-agent retailer prompt this run used — internal users only,
                  retailer runs only. Per-product fallbacks show in the product quick view. */}
                {canSeeRetailerPrompt && header.runConfig?.retailer && (
                  <Badge
                    data-testid="batch-detail-prompt-badge"
                    variant="outline"
                    className="h-8 shrink-0 rounded-md px-2.5 text-xs"
                  >
                    Prompt: {header.runConfig.retailer}
                  </Badge>
                )}
                {/* Every language this run delivers — `en-US` for every English-only batch. */}
                {!isMetaAds && (
                  <Badge
                    data-testid="batch-detail-languages-badge"
                    variant="outline"
                    className="h-8 shrink-0 rounded-md px-2.5 text-xs"
                  >
                    {t('batchDetail.languages', {
                      locales: header.locales
                        .map((code) => formatLocale(code, localeNames.data))
                        .join(' · '),
                    })}
                  </Badge>
                )}
                {/* Languages — the run's translated locales and a translate-more action.
                  Internal users only (self-gated), Google runs only: translation is GMC content. */}
                {!isMetaAds && header.workflowId && (
                  <BatchTranslationsDialog
                    workflowId={header.workflowId}
                    configuredLocales={header.runConfig?.targetLocales}
                    className="h-8 w-8"
                  />
                )}
                {/* Admin-only Archive / Unarchive (List Archive). Never pre-disabled:
                  the server probes the run, and a live one opens a Stop / Reconcile dialog. */}
                {canManageArchive && (
                  <>
                    <EnrichmentJobArchiveControl
                      jobId={batchId}
                      archivedAt={header.archivedAt}
                      buttonVariant="outline"
                      className="h-8 w-8 shrink-0"
                      testId="batch-detail-archive-button"
                    />
                    {/* Every archive / restore of this batch, newest first (detail payloads only). */}
                    {/* A run that still reads live may be dead: offer the non-destructive
                      reconcile probe, which corrects the status to Failed when it is. */}
                    {(header.status === 'in-progress' || header.status === 'running') && (
                      <ReconcileJobButton jobId={batchId} className="h-8 w-8" />
                    )}
                    <ArchiveHistoryPopover history={header.archiveHistory} className="h-8 w-8" />
                  </>
                )}
                {/* Super-admin-only: toggles the raw-IDs debug panel. Hidden
                  entirely unless the `debugger-int` flag is on and the user is a
                  super-admin, so it never surfaces for customers. */}
                {showDebug && (
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="batch-detail-debug-button"
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 shrink-0 cursor-pointer${
                          isDebugOpen ? 'border-primary text-primary' : ''
                        }`}
                        onClick={() => {
                          setIsDebugOpen((open) => !open);
                        }}
                        aria-pressed={isDebugOpen}
                        aria-label={t('batchDetailHeader.debug')}
                      >
                        <Bug className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('batchDetailHeader.debug')}</TooltipContent>
                  </Tooltip>
                )}
                {/* Manual refetch of every query feeding this page — one
                  invalidation of the catalog-operations subtree covers all four
                  phases (header, coverage, optimization-quality, live-stage) plus
                  insights and the signal-coverage cards, since they all nest under
                  it. Complements the focus-refetch (the phase queries are
                  always-stale) for when the data changes while the tab stays focused. */}
                <RefreshIconButton
                  testId="batch-detail-refresh-button"
                  variant="outline"
                  className="h-8 w-8"
                  label={t('batchDetailHeader.refresh')}
                  onRefresh={() =>
                    queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all })
                  }
                />
                {/* Compare Delivery — opens the pre-enrichment / enriched / delivered
                  side-by-side view. Hidden entirely unless the `compare-delivery`
                  gate is on AND the Delivered stage is actually complete (see
                  `getDeliveryCompareEligibility`) — a batch that never pushed a
                  feed has nothing to compare. */}
                {canCompareDelivery && (
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="batch-detail-compare-delivery-button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0 cursor-pointer"
                        onClick={handleOpenCompare}
                        aria-label={t('batchDetailHeader.compareDelivery')}
                      >
                        <Columns3 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('batchDetailHeader.compareDelivery')}</TooltipContent>
                  </Tooltip>
                )}
                {/* Same run-scoped CSV export as the products page. Icon-only with
                  a tooltip here to match the open-workflow icon button beside it
                  and keep the header's action cluster compact. */}
                <ExportDownloadButton
                  sourceWorkflowId={header.exportWorkflowId ?? null}
                  label={exportLabel}
                  connector={exportConnector}
                  iconOnly
                  variant="outline"
                  className="h-8 w-8 shrink-0"
                  {...(header.runConfig?.contentAttributes
                    ? { batchAttributeKeys: header.runConfig.contentAttributes }
                    : {})}
                />
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="batch-detail-open-workflow-button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                      onClick={handleOpenWorkflow}
                      isLoading={resumingId === header.batchId}
                      aria-label={t('batchDetailHeader.openEnrichmentWorkflow')}
                    >
                      {resumingId !== header.batchId && (
                        <SquareArrowOutUpRight className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('batchDetailHeader.openEnrichmentWorkflow')}</TooltipContent>
                </Tooltip>
              </>
            }
          />
        </div>

        <div className="scrollbar-transparent min-h-0 flex-1 space-y-6 overflow-y-auto">
          {/* Surface a stale-source preview failure the parent rollup masks — only
              when the run itself isn't already shown as Failed (that's covered by
              the status chip + retry flow). */}
          {header.status !== 'failed' && (
            <ScheduledPreviewFailureBanner status={scheduledJobStatus} />
          )}
          {/* Validation strip (collapsed one-liner, like the Cost estimate strip) — above the tabs, so on every tab. */}
          <RunValidationStrip target={header} />
          {funnel && (
            <FunnelVerdictBanner
              funnel={funnel}
              health={funnelRunDetail?.health}
              bannerError={bannerError}
              onRetry={() => {
                setIsRetryDialogOpen(true);
              }}
              retryLoading={isFailurePointsLoading}
              retryUnavailableReason={funnelRetryUnavailableReason}
              onReset={() => {
                setIsFunnelResetOpen(true);
              }}
              resetPending={resetFunnel.isPending}
              onReviewDelivery={handleReviewDelivery}
            />
          )}
          {isFunnelAllowed && isFunnelError && (
            <p data-testid="batch-detail-funnel-error" className="text-muted-foreground text-sm">
              {t('batchFunnel.loadError')}
            </p>
          )}
          <BatchTopMetrics metrics={topMetrics} />
          {/* Run paused at the pre-enrichment cost gate — sits right above the timeline. */}
          <CostApprovalPendingBanner workflowId={costWorkflowId} runActive={costRunActive} />
          {batchRuns && retryState && (
            <BatchRetryBanner
              runs={batchRuns}
              retryAwaitingCostApproval={retryAwaitingCostApproval}
              state={retryState}
              needsAttention={batchNeedsAttention}
              onViewProducts={() => {
                showNeedsAttentionProducts();
              }}
              onExclude={() => {
                setIsExcludeOpen(true);
              }}
              onUndoExclusion={() => {
                undoExclusion.mutate();
              }}
              undoPending={undoExclusion.isPending}
            />
          )}
          <BatchPipelineStages
            stages={stages}
            {...(carryoverNote
              ? {
                  carryoverNote: {
                    text: carryoverNote,
                    tone: 'muted' as const,
                    onClick: showCarryover,
                    ...(carryoverTitle ? { title: carryoverTitle } : {}),
                  },
                }
              : {})}
            {...(retryState?.paused
              ? { enrichedNote: { text: 'resume to continue', tone: 'muted' as const } }
              : retryState?.retryRunning
                ? {
                    enrichedNote: {
                      text: retryAwaitingCostApproval
                        ? `Retry #${String(retryState.activeRun?.attempt ?? retryState.attemptsUsed)} · waiting for cost approval`
                        : retryState.activeRun?.done != null
                          ? `${retryState.activeRun.done.toLocaleString()} of ${runTotal(retryState.activeRun).toLocaleString()} done`
                          : `Retry #${String(retryState.activeRun?.attempt ?? retryState.attemptsUsed)} · ${(retryState.activeRun ? runTotal(retryState.activeRun) : retryState.left).toLocaleString()} products`,
                      tone: 'muted' as const,
                    },
                  }
                : retryState && retryState.left > 0 && retryState.banner
                  ? {
                      enrichedNote: {
                        text: `${retryState.left.toLocaleString()} not enriched yet`,
                        tone: 'warning' as const,
                        onClick: () => {
                          showNeedsAttentionProducts();
                        },
                      },
                    }
                  : retryState && retryState.excluded > 0
                    ? {
                        enrichedNote: {
                          text: `${retryState.excluded.toLocaleString()} excluded`,
                          tone: 'muted' as const,
                        },
                      }
                    : {})}
            {...(retryState &&
            retryState.left > 0 &&
            (retryState.banner === 'not-enriched' || retryState.banner === 'stuck')
              ? { deliveryHint: `approve without ${retryState.left.toLocaleString()}` }
              : {})}
            // A rejected run is terminal — never offer an actionable approve. The
            // backend sets REJECTED on both status and pipelineStatus (so
            // isAwaitingApproval is already false), but guard defensively for the
            // rollout window where the two signals could briefly disagree.
            // A running or paused retry blocks approval server-side (409); don't offer it.
            awaitingApproval={
              header.isAwaitingApproval &&
              !header.isRejected &&
              !retryState?.retryRunning &&
              !retryState?.paused
            }
            deliveryInProgress={header.isDelivering}
            deliveryLocked={header.isTrial && header.isAwaitingApproval}
            onReviewDelivery={handleReviewDelivery}
            onRetry={() => {
              setIsRetryDialogOpen(true);
            }}
            retryReady={!!failurePoints?.failurePoints.length}
            retryLoading={isFailurePointsLoading}
            // Enriched card → same drill-in as the "Newly Enriched Products" row.
            onEnrichedClick={() => {
              handleDrillIn('Newly Enriched Products');
            }}
            {...(canCompareDelivery ? { onCompare: handleOpenCompare } : {})}
          />
          <CostEstimateCard
            workflowId={costWorkflowId}
            runActive={costRunActive}
            enrichmentPhase={costEnrichmentPhase}
            plannedCost={header.plannedCost ?? null}
            collapsible
          />
          {funnel && (
            <FunnelIssues
              issues={funnel.issues ?? []}
              onDrill={openFunnelDrill}
              stageErrors={currentStageErrors}
              earlierAttempts={earlierStageErrors}
            />
          )}
          <FunnelProductsSheet
            // Keyed on the target so paging / trace state resets per drill-in.
            key={funnelDrill ? JSON.stringify(funnelDrill) : 'closed'}
            jobId={batchId}
            target={funnelDrill}
            onClose={() => {
              setFunnelDrill(null);
            }}
          />
          {funnel?.actions?.resetPath && (
            <ConfirmDialog
              open={isFunnelResetOpen}
              testId="batch-detail-funnel-reset-confirm-dialog"
              onOpenChange={setIsFunnelResetOpen}
              title={t('batchFunnel.reset.confirmTitle')}
              description={t('batchFunnel.reset.confirmDescription')}
              confirmLabel={t('batchFunnel.reset.confirmAction')}
              isPending={resetFunnel.isPending}
              onConfirm={() => {
                const resetPath = funnel.actions?.resetPath;
                if (!resetPath) return;
                resetFunnel.mutate(resetPath, {
                  onSettled: () => {
                    setIsFunnelResetOpen(false);
                  },
                });
              }}
            />
          )}
          <DeliveryApprovalPanel
            open={isDeliveryApprovalOpen}
            jobId={batchId}
            pipelineWorkflowId={header.pipelineWorkflowId ?? null}
            platform={enrichmentPlatform}
            {...(header.runConfig ? { runConfig: header.runConfig } : {})}
            {...(header.alwaysOnAttributes
              ? { alwaysOnAttributes: header.alwaysOnAttributes }
              : {})}
            onClose={() => {
              setIsDeliveryApprovalOpen(false);
            }}
          />
          {!source && (
            <>
              <BatchRetryDialog
                jobId={batchId}
                open={batchRetryDialog.open}
                onOpenChange={(open) => {
                  setBatchRetryDialog((prev) => ({ ...prev, open }));
                }}
                {...(batchRetryDialog.scope ? { initialScope: batchRetryDialog.scope } : {})}
                {...(batchRetryDialog.fromRunId ? { fromRunId: batchRetryDialog.fromRunId } : {})}
              />
              <PostStepRetryDialog
                jobId={batchId}
                open={postStepRetry.open}
                step={postStepRetry.step}
                {...(postStepRetry.scope ? { initialScope: postStepRetry.scope } : {})}
                onOpenChange={(open) => {
                  setPostStepRetry((prev) => ({ ...prev, open }));
                }}
              />
              <ExcludeRemainingDialog
                jobId={batchId}
                open={isExcludeOpen}
                onOpenChange={setIsExcludeOpen}
              />
            </>
          )}
          <WorkflowRetryDialog
            open={isRetryDialogOpen}
            jobId={batchId}
            workflowId={header.batchId}
            failurePoints={failurePoints}
            onClose={() => {
              setIsRetryDialogOpen(false);
            }}
          />
          <ConfirmDialog
            open={isRecomputeDialogOpen}
            testId="batch-detail-performance-signals-recompute-confirm-dialog"
            onOpenChange={setIsRecomputeDialogOpen}
            title={t('performanceSignals.recompute.confirmTitle')}
            description={t('performanceSignals.recompute.confirmDescription')}
            confirmLabel={t('performanceSignals.recompute.confirmAction')}
            isPending={isRecomputing}
            onConfirm={() => {
              // Closed on confirm rather than on settle: the run is tracked by
              // the button's own pending state, so holding the dialog open would
              // block the tab for a workflow that usually lands in a second.
              setIsRecomputeDialogOpen(false);
              startRecompute();
            }}
          />
          <ConfirmDialog
            open={isCoverageRecomputeDialogOpen}
            testId="batch-detail-coverage-recompute-confirm-dialog"
            onOpenChange={setIsCoverageRecomputeDialogOpen}
            title={t('coverageSignals.recompute.confirmTitle')}
            description={t('coverageSignals.recompute.confirmDescription')}
            confirmLabel={t('coverageSignals.recompute.confirmAction')}
            isPending={isCoverageRecomputing}
            onConfirm={() => {
              // Closed on confirm, not on settle — the button's own pending state
              // tracks the run, so holding the dialog open would block the tab.
              setIsCoverageRecomputeDialogOpen(false);
              startCoverageRecompute();
            }}
          />
          <InsightMembershipDrawer
            // Keyed on category:label so switching rows fully remounts the
            // drawer's internal filter state instead of needing an effect to
            // reset it.
            key={
              selectedMembership
                ? `${selectedMembership.category}:${selectedMembership.label}`
                : 'none'
            }
            open={isMembershipDrawerOpen}
            onOpenChange={handleMembershipDrawerOpenChange}
            workflowId={header.batchId}
            category={selectedMembership?.category ?? null}
            label={selectedMembership?.label ?? null}
            // A restored drawer has no clicked row, so its stats come from the
            // loaded insights by label (null until they arrive).
            headerStats={
              selectedMembership
                ? (selectedMembership.headerStats ??
                  findMembershipHeaderStats(
                    selectedMembership.category,
                    selectedMembership.label,
                    insights,
                  ))
                : null
            }
            jobId={batchId}
            returnTab={activeTab}
            {...(selectedMembership?.initialGroup
              ? { initialGroup: selectedMembership.initialGroup }
              : {})}
          />

          {isUnprocessedFailure ? (
            <div
              data-testid="batch-detail-not-processed-empty"
              className="flex flex-col items-center justify-center gap-4 py-16 text-center"
            >
              <div className="bg-destructive/10 flex h-16 w-16 items-center justify-center rounded-full">
                <Ban className="text-destructive h-8 w-8" aria-hidden="true" />
              </div>
              <div className="space-y-1">
                <p className="text-foreground text-base font-semibold">
                  {t('batchNotProcessed.title')}
                </p>
                <p className="text-muted-foreground max-w-sm text-sm">
                  {t('batchNotProcessed.subtitle')}
                </p>
              </div>
              <Button
                data-testid="batch-detail-not-processed-retry-button"
                variant="outline"
                className="border-error text-error hover:text-error cursor-pointer disabled:cursor-not-allowed"
                onClick={() => {
                  setIsRetryDialogOpen(true);
                }}
                isLoading={isFailurePointsLoading}
                disabled={!failurePoints?.failurePoints.length}
              >
                {t('retry.button')}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <SegmentedTabs
                value={activeTab}
                onChange={handleTabChange}
                options={detailTabs}
                ariaLabel="Batch detail sections"
                panelId="batch-detail-panel"
                className="sm:max-w-full"
                testId="batch-detail-tabs"
              />

              <div
                id="batch-detail-panel"
                role="tabpanel"
                data-testid={`batch-detail-panel-${activeTab}`}
                className="space-y-6"
              >
                {/* Delivery Health — phase-3 coverage read; skeletons until it
                    lands, so the shell above never waits on it. */}
                {activeTab === 'delivery-health' &&
                  (isCoverageDetailError ? (
                    <TabLoadError t={t} />
                  ) : isCoverageDetailLoading || !coverageDetail ? (
                    <DeliveryHealthSkeleton testId="batch-detail-delivery-health-loading" />
                  ) : (
                    <>
                      <BatchProcessingDetails
                        columns={withCarryoverRows(coverageDetail.processing, carryover)}
                        onDrillIn={handleDrillIn}
                      />
                      <EmptyStateOverlay
                        active={notFullyProcessed}
                        icon={Layers}
                        message="Batch is not fully processed!"
                        testId="batch-detail-not-processed-overlay"
                      >
                        <BatchNeedsAttention
                          total={coverageDetail.attention.total}
                          items={coverageDetail.attention.items}
                          isTrial={header.isTrial}
                          {...(batchNeedsAttention && retryState
                            ? {
                                retry: {
                                  needsAttention: batchNeedsAttention,
                                  canRetry: retryState.has('RETRY_REMAINING'),
                                  canUndo: retryState.has('UNDO_EXCLUSION'),
                                  stuck: retryState.banner === 'stuck',
                                  onViewProducts: showNeedsAttentionProducts,
                                  onRetry: (scope: BatchRetryScope) => {
                                    openBatchRetry({ scope });
                                  },
                                  onUndo: () => {
                                    undoExclusion.mutate();
                                  },
                                  undoPending: undoExclusion.isPending,
                                },
                              }
                            : {})}
                        />
                      </EmptyStateOverlay>
                    </>
                  ))}

                {/* Performance Signals */}
                {activeTab === 'performance-signals' &&
                  (isInsightsLoading ? (
                    <PerformanceSignalsSkeleton />
                  ) : (
                    <>
                      {/* Recompute — re-runs the signals workflow for this batch.
                        Confirmed first: it rewrites the persisted rows every
                        card on this tab reads. Hidden when the run failed at or
                        before enrichment — there's no enrichment output to
                        recompute from. Paired with a section title/subtitle on
                        the left, matching Coverage Signals' header bar below —
                        a lone right-aligned button otherwise leaves the row
                        mostly blank. */}
                      {!failedBeforeEnrichment && (
                        <div className="flex items-end justify-between gap-4">
                          <div className="space-y-1">
                            <h2
                              data-testid="batch-detail-performance-signals-title"
                              className="text-foreground text-base font-medium"
                            >
                              {t('performanceSignals.sectionTitle')}
                            </h2>
                            <p className="text-muted-foreground text-sm">
                              {t('performanceSignals.sectionSubtitle')}
                            </p>
                          </div>
                          <Button
                            data-testid="batch-detail-performance-signals-recompute-button"
                            variant="outline"
                            size="sm"
                            className="shrink-0 cursor-pointer"
                            onClick={() => {
                              setIsRecomputeDialogOpen(true);
                            }}
                            isLoading={isInsightsRecomputing}
                            // `connectorCode` is still being backfilled and the
                            // trigger requires it — without one the POST is a
                            // guaranteed 422, so don't offer the action.
                            disabled={!header.batchId || !header.connectorCode}
                          >
                            {/* Hidden while loading — the Button already renders its
                                own spinner, so keeping this would show two icons. */}
                            {!isInsightsRecomputing && (
                              <RefreshCw className="h-4 w-4" aria-hidden="true" />
                            )}
                            {t('performanceSignals.recompute.button')}
                          </Button>
                        </div>
                      )}

                      {isInsightsRecomputing ? (
                        <PerformanceSignalsRecomputing t={t} />
                      ) : (
                        <>
                          {/* What enrichment added — 4-card headline summary */}
                          <PerformanceSignalsStatsRow cards={performanceCards} />

                          {/* Popular Product Terms */}
                          <div
                            data-testid="batch-detail-popular-terms-card"
                            className="bg-background rounded-xl px-5 py-6"
                          >
                            <AIPerformanceInsights
                              testId="batch-detail-popular-terms"
                              title={t('performanceSignals.popularTerms.title')}
                              subtitle={t('performanceSignals.popularTerms.subtitle')}
                              tableTitle={t('performanceSignals.popularTerms.tableTitle')}
                              tableDescription={t(
                                'performanceSignals.popularTerms.tableDescription',
                              )}
                              items={insights?.popularTerms ?? []}
                              showLogo
                              isGoogleAdsConnected={isGoogleAdsConnected}
                              hideImpressionShare
                              onRowClick={(item) => {
                                setSelectedMembership({
                                  category: 'POPULAR_TERM',
                                  label: item.term,
                                  headerStats: buildTermHeaderStats(item),
                                });
                                setIsMembershipDrawerOpen(true);
                              }}
                            />
                          </div>

                          {/* Popular Product Attributes */}
                          <div
                            data-testid="batch-detail-popular-attributes-card"
                            className="bg-background rounded-xl px-5 py-6"
                          >
                            <AttributeScoreList
                              testId="batch-detail-popular-attributes"
                              title={t('performanceSignals.popularAttributes.title')}
                              subtitle={t('performanceSignals.popularAttributes.subtitle')}
                              attributes={insights?.popularAttributes ?? []}
                              hideComparisonLabel
                              {...(insights?.attributeSampleSize === undefined
                                ? {}
                                : { sampleSize: insights.attributeSampleSize })}
                              {...(insights?.attributeCategorySizes === undefined
                                ? {}
                                : { categorySizes: insights.attributeCategorySizes })}
                            />
                          </div>

                          {/* Coverage & Trends */}
                          <div
                            data-testid="batch-detail-coverage-trends-card"
                            className="bg-background rounded-xl px-5 py-6"
                          >
                            <div className="flex flex-col gap-4">
                              <div className="flex w-full items-start justify-between gap-6">
                                <div className="flex w-full flex-col gap-1">
                                  <div className="flex w-full flex-row items-center justify-between gap-4">
                                    <div className="flex flex-row items-center gap-4">
                                      <div className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                                        <LilyLogoMark className="h-6 w-6" />
                                      </div>
                                      <p className="text-foreground text-base font-medium">
                                        {t('performanceSignals.insightsByLily.title')}
                                      </p>
                                    </div>
                                    {/* Clicking a legend item solos that series across
                                    both lists below; both show by default, and
                                    clicking the active one returns to both. */}
                                    <div className="flex shrink-0 items-center gap-4">
                                      <button
                                        type="button"
                                        data-testid="batch-detail-legend-before-toggle"
                                        aria-pressed={insightsBeforeVisible}
                                        onClick={() => {
                                          setInsightsSolo((prev) =>
                                            prev === 'before' ? null : 'before',
                                          );
                                        }}
                                        className={`flex cursor-pointer items-center gap-1.5 transition-opacity ${insightsBeforeVisible ? 'opacity-100' : 'opacity-40'}`}
                                      >
                                        <span
                                          className="bg-warning h-3 w-3 shrink-0 rounded-full"
                                          aria-hidden="true"
                                        />
                                        <span className="text-text-date-pill text-sm">
                                          {t('performanceSignals.legend.before')}
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        data-testid="batch-detail-legend-after-toggle"
                                        aria-pressed={insightsAfterVisible}
                                        onClick={() => {
                                          setInsightsSolo((prev) =>
                                            prev === 'after' ? null : 'after',
                                          );
                                        }}
                                        className={`flex cursor-pointer items-center gap-1.5 transition-opacity ${insightsAfterVisible ? 'opacity-100' : 'opacity-40'}`}
                                      >
                                        <span
                                          className="bg-primary h-3 w-3 shrink-0 rounded-full"
                                          aria-hidden="true"
                                        />
                                        <span className="text-text-date-pill text-sm">
                                          {t('performanceSignals.legend.after')}
                                        </span>
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-muted-foreground text-xs font-light">
                                    {t('performanceSignals.insightsByLily.subtitle')}
                                  </p>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                <AttributeComparisonList
                                  testId="batch-detail-occasions"
                                  title={t('performanceSignals.coverageAndTrends.occasions.title')}
                                  subtitle={t(
                                    'performanceSignals.coverageAndTrends.occasions.subtitle',
                                  )}
                                  items={insights?.occasions ?? []}
                                  maxValue={insights?.occasionsMax ?? 100}
                                  formatValue={(v) => `${String(v)}%`}
                                  hideLegend
                                  showBefore={insightsBeforeVisible}
                                  showAfter={insightsAfterVisible}
                                  getRowClickHandler={(item) => () => {
                                    setSelectedMembership({
                                      category: 'OCCASION',
                                      label: item.title,
                                      headerStats: buildOccasionHeaderStats(item),
                                    });
                                    setIsMembershipDrawerOpen(true);
                                  }}
                                />
                                <AttributeComparisonList
                                  testId="batch-detail-trends"
                                  title={t('performanceSignals.coverageAndTrends.trends.title')}
                                  subtitle={t(
                                    'performanceSignals.coverageAndTrends.trends.subtitle',
                                  )}
                                  items={trendItems}
                                  maxValue={insights?.trendsMax ?? 1}
                                  formatValue={String}
                                  hideLegend
                                  showBefore={insightsBeforeVisible}
                                  showAfter={insightsAfterVisible}
                                  // Only macro/micro-tagged rows have a determinable
                                  // membership category — untagged "Top Trends" rows
                                  // have no matching category in the endpoint's enum,
                                  // so they stay non-interactive rather than querying
                                  // with a made-up value.
                                  getRowClickHandler={(item) => {
                                    const trendKind = item.trendKind;
                                    if (!trendKind) return undefined;
                                    const category: InsightMembershipCategory =
                                      trendKind === 'macro' ? 'MACRO_TREND' : 'MICRO_TREND';
                                    return () => {
                                      setSelectedMembership({
                                        category,
                                        label: item.title,
                                        headerStats: buildTrendHeaderStats(item, trendKind),
                                      });
                                      setIsMembershipDrawerOpen(true);
                                    };
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  ))}

                {/* Enrichment Coverage */}
                {activeTab === 'enrichment-coverage' && (
                  <>
                    {/* Stats row + (trial) legacy coverage cards both come from the
                        phase-3 coverage read; skeleton until it lands so the shell
                        above never waits on it. */}
                    {isCoverageDetailError ? (
                      <TabLoadError t={t} />
                    ) : isCoverageDetailLoading || !coverageDetail ? (
                      <>
                        <Skeleton
                          data-testid="batch-detail-enrichment-coverage-loading"
                          className="h-24 w-full rounded-xl"
                        />
                        {isTrialBatch && <CoverageSignalsSkeleton />}
                      </>
                    ) : (
                      <>
                        <CoverageStatsRow stats={coverageDetail.enrichmentCoverage.stats} />
                        {isTrialBatch && (
                          // Trial batches show the legacy Coverage by Category /
                          // Product Attribute cards; the batch-scoped Coverage
                          // Signals section below is hidden for them.
                          <div
                            data-testid="batch-detail-trial-coverage-grid"
                            className="grid grid-cols-1 gap-4 lg:grid-cols-2"
                          >
                            <CategoryCoverageList
                              rows={coverageDetail.enrichmentCoverage.categories}
                            />
                            <AttributeCoverageList
                              rows={coverageDetail.enrichmentCoverage.attributes}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {!isTrialBatch && (
                      <>
                        {/* Coverage Signals — a separate, batch-scoped read from
                          workflow-service (`{workflowId}/coverage`). Kept below the
                          cards above rather than replacing them: those come from
                          the job-detail payload and answer a different question. */}
                        <div className="flex items-end justify-between gap-4 pt-2">
                          <div className="space-y-1">
                            <h2
                              data-testid="batch-detail-coverage-signals-title"
                              className="text-foreground text-base font-medium"
                            >
                              {t('coverageSignals.sectionTitle')}
                            </h2>
                            <p className="text-muted-foreground text-sm">
                              {t('coverageSignals.sectionSubtitle')}
                            </p>
                          </div>
                          {/* Recompute — re-measures the batch on integration-service
                        and rewrites the rows these four cards read. Confirmed
                        first, same as the Performance Signals recompute. Hidden
                        when the run failed at or before enrichment — there's no
                        enrichment output to recompute from. */}
                          {!failedBeforeEnrichment && (
                            <Button
                              data-testid="batch-detail-coverage-signals-recompute-button"
                              variant="outline"
                              size="sm"
                              className="shrink-0 cursor-pointer"
                              onClick={() => {
                                setIsCoverageRecomputeDialogOpen(true);
                              }}
                              // Also spins for the automatic empty-coverage
                              // recompute, so the button doesn't invite a second,
                              // redundant run while one is already in flight.
                              isLoading={isCoverageRecomputing || isCoverageAutoRecomputing}
                              // The trigger requires a connectorCode and 422s without
                              // one, so don't offer the action while it's unresolved.
                              disabled={
                                !header.batchId ||
                                !header.connectorCode ||
                                isCoverageAutoRecomputing
                              }
                            >
                              {/* Hidden while loading — the Button already renders its
                                  own spinner, so keeping this would show two icons. */}
                              {!(isCoverageRecomputing || isCoverageAutoRecomputing) && (
                                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                              )}
                              {t('coverageSignals.recompute.button')}
                            </Button>
                          )}
                        </div>

                        {isCoverageRecomputing || isCoverageAutoRecomputing ? (
                          <CoverageSignalsRecomputing t={t} />
                        ) : isCoverageLoading || isAwaitingCoverage ? (
                          <CoverageSignalsSkeleton />
                        ) : (
                          <div
                            data-testid="batch-detail-coverage-signals-grid"
                            className="grid grid-cols-1 gap-4 lg:grid-cols-2"
                          >
                            {/* Category Enrichment Rate. META_ADS runs hide the
                                Product Detail Changes card beside it, so it takes
                                the full row instead of a half. */}
                            <div className={isMetaAds ? 'lg:col-span-2' : undefined}>
                              <CoverageCategorySignalList rows={coverage?.categories ?? []} />
                            </div>
                            {/* Product Detail Changes — hidden for META_ADS runs. */}
                            {!isMetaAds && (
                              <ProductDetailChangesCard
                                rows={
                                  coverage?.productDetail ?? {
                                    added: [],
                                    changed: [],
                                    removed: [],
                                    renamed: [],
                                  }
                                }
                              />
                            )}
                            {/* Field Coverage spans the full width below the cards above. */}
                            <div className="lg:col-span-2">
                              <CoverageAttributeSignalList rows={coverageAttributeRows} />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Optimization Quality — phase-4, the heaviest read; fetched
                    lazily only when this tab is opened. */}
                {activeTab === 'optimization-quality' &&
                  (isOptimizationLoading || !optimization ? (
                    <OptimizationQualitySkeleton />
                  ) : (
                    <div
                      data-testid="batch-detail-optimization-quality-card"
                      className="bg-background rounded-xl px-5 py-6"
                    >
                      {/* Quality Score Meter */}
                      <div className="mb-3 space-y-1">
                        <h2
                          data-testid="batch-detail-overall-score-title"
                          className="text-foreground text-base font-medium"
                        >
                          {t('qualityScore.overallScore.title')}
                        </h2>
                        <p className="text-muted-foreground text-sm">
                          {t('qualityScore.overallScore.subtitle')}
                        </p>
                      </div>
                      <QualityScoreMeter
                        testId="batch-detail-quality-score-meter"
                        beforeScore={optimization.sampleQualityScore.beforeScore}
                        afterScore={optimization.sampleQualityScore.afterScore}
                        // scoreNote={t('qualityScore.scoreNote')}
                      />

                      <Separator className="my-4" />

                      {/* 3Cs, R & D */}
                      <RD3C
                        testId="batch-detail-rd3c"
                        title={t('qualityScore.rd3c.catalogOperationsTitle')}
                        subtitle={t('qualityScore.rd3c.catalogOperationsDescription')}
                        items={transformDimensionScoresToRD3CItems(
                          optimization.dimensionScores,
                          RD3C_DIMENSION_LABEL_KEYS.map(({ titleKey, descriptionKey }) => ({
                            title: t(titleKey),
                            description: t(descriptionKey),
                          })),
                        )}
                      />

                      <Separator className="my-4" />

                      {/* Content Attribute Scores. The sample-size note is appended
                          only when the run reported a non-zero curated pre-scored
                          count — a 0-product sample note reads as broken. */}
                      <AttributeScoreList
                        testId="batch-detail-content-attribute-scores"
                        title={t('qualityScore.contentAttributeScores.title')}
                        subtitle={
                          (optimization.curatedPreScoredCount ?? 0) > 0
                            ? `${t('qualityScore.contentAttributeScores.subtitle')} · ${t(
                                'qualityScore.contentAttributeScores.sampleNote',
                                { sampleSize: optimization.curatedPreScoredCount },
                              )}`
                            : t('qualityScore.contentAttributeScores.subtitle')
                        }
                        attributes={transformQualityScorePanelToItems(
                          optimization.qualityScore.panels[0]?.rows ?? [],
                        )}
                        hideComparisonLabel
                      />

                      <Separator className="my-4" />

                      {/* Category-Level Quality Score */}
                      <AttributeScoreList
                        testId="batch-detail-category-level-scores"
                        title={t('qualityScore.categoryLevelScores.title')}
                        subtitle={t('qualityScore.categoryLevelScores.subtitle')}
                        attributes={transformQualityScorePanelToItems(
                          optimization.qualityScore.panels[1]?.rows ?? [],
                          10,
                        )}
                        hideComparisonLabel
                      />
                    </div>
                  ))}

                {/* Run details — the product funnel, where products ended up, and the
                    secondary sections (quality, cost, vs last batch, markets, side
                    branches, activity). */}
                {/* Run details, first: the batch's runs — the original run and its retry children
                    (batch pause / retry), products beside the run list. */}
                {activeTab === 'run-details' && showRunsSection && (
                  <BatchRunsTab
                    jobId={batchId}
                    onRetryRun={(fromRunId) => {
                      openBatchRetry({ fromRunId });
                    }}
                    onRetryRemaining={() => {
                      openBatchRetry({ scope: 'REMAINING' });
                    }}
                    focusProducts={focusRunProducts}
                    onProductsFocused={clearFocusRunProducts}
                    focusCarryover={focusCarryover}
                    onCarryoverFocused={clearFocusCarryover}
                    onExclude={() => {
                      setIsExcludeOpen(true);
                    }}
                    onRetryPostStep={(step, scope) => {
                      setPostStepRetry({ open: true, step, ...(scope ? { scope } : {}) });
                    }}
                  />
                )}

                {activeTab === 'run-details' &&
                  isFunnelAllowed &&
                  (funnel ? (
                    <FunnelRunDetails
                      funnel={funnel}
                      costWorkflowId={costWorkflowId}
                      plannedCost={header.plannedCost ?? null}
                      onDrill={openFunnelDrill}
                      errorTally={tallyStageErrors(currentStageErrors)}
                    />
                  ) : isFunnelError ? (
                    <TabLoadError t={t} />
                  ) : (
                    <DeliveryHealthSkeleton testId="batch-detail-run-details-loading" />
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Floating, non-modal debug panel — `fixed` to the viewport, so it
            stays pinned bottom-right regardless of the scroll region above. */}
        {showDebug && (
          <BatchDebugPanel
            open={isDebugOpen}
            ids={debugIds}
            testId="batch-detail-debug-panel"
            onClose={() => {
              setIsDebugOpen(false);
            }}
          />
        )}
      </div>
    </CatalogOperationsLayout>
  );
}
